import { gambitRequest } from './gambitTransport';
import { loadGambitAccess } from './gambitAccount';
import type { StudioState } from '../components/studio/studioModel';
import { unfinishedGambitChapter, UnfinishedGambitChapter } from '../components/studio/studioContinuation';

type Request = (route: string, options?: RequestInit) => Promise<any>;
async function withGambitSession<T>(ownerId: string, run: (request: Request) => Promise<T>, signal?: AbortSignal): Promise<T | null> {
  const access = await loadGambitAccess(signal);
  if (!access.allowed || access.userId !== ownerId) return null;
  const controller = new AbortController();
  const cancel = () => controller.abort();
  if (signal?.aborted) cancel();
  signal?.addEventListener('abort', cancel, { once: true });
  const timeout = setTimeout(cancel, 120000);
  try {
    return await run((route, options = {}) => gambitRequest(route, { ...options, signal: controller.signal }));
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', cancel);
  }
}

/** Read draft progress without mounting Studio, navigating, or starting a job. */
export function readGambitWorkspace(ownerId: string, signal?: AbortSignal): Promise<StudioState | null> {
  return withGambitSession(ownerId, request => request('/workspace'), signal);
}

/** Refresh saved provider receipts and release unused holds, without generation. */
export function reconcileGambitUsage(ownerId: string, signal?: AbortSignal): Promise<{ updated: number; pending: number; failed: number } | null> {
  return withGambitSession(ownerId, async request => {
    let updated = 0, result = { updated: 0, pending: 0, failed: 0 };
    for (let batch = 0; batch < 5; batch++) {
      result = await request('/usage/reconcile', { method: 'POST' });
      updated += result.updated;
      if (!result.updated || !result.pending || result.failed) break;
    }
    return { ...result, updated };
  }, signal);
}

export type GambitRestart = {
  ownerId: string; requestId: string; projectId: string; chapterId: string; expectedSequenceId: string;
};

/** Check availability before charging for Classic, without discarding anything. */
export async function prepareGambitRestart(ownerId: string, draft: UnfinishedGambitChapter, storyId: string, chapterIds: string[], clientStoryId: string): Promise<GambitRestart> {
  const workspace = await readGambitWorkspace(ownerId);
  if (!workspace) throw new Error('Could not check the Gambit draft. Please try again.');
  const current = unfinishedGambitChapter(workspace, storyId, chapterIds);
  const project = workspace.projects.find(project => project.id === draft.projectId);
  const chapter = project?.chapters.find(chapter => chapter.id === draft.chapterId);
  if (!chapter || current?.chapterId !== draft.chapterId || current.projectId !== draft.projectId || chapter !== project?.chapters.at(-1))
    throw new Error('The Gambit chapter changed. Reopen Create Next Chapter before restarting.');
  if (project?.chapters.some(chapter => chapter.jobs?.some(job => ['queued', 'running'].includes(job.status))))
    throw new Error('Wait for the current Gambit generation to finish before restarting in Fable Go.');
  return { ownerId, projectId: draft.projectId, chapterId: draft.chapterId,
    expectedSequenceId: chapter.activeSequenceId, requestId: `classic-restart:${clientStoryId}` };
}

/** Call only after Classic accepts creation. Retrying this cannot create a chapter. */
export async function replaceUnfinishedGambitChapter(restart: GambitRestart): Promise<StudioState> {
  const workspace = await withGambitSession(restart.ownerId, async request => {
    await request('/actions', { method: 'POST', headers: {
      'Content-Type': 'application/json', 'Idempotency-Key': restart.requestId,
    }, body: JSON.stringify({ action: {
      type: 'discard-unfinished-chapter', target: { projectId: restart.projectId, chapterId: restart.chapterId },
      expectedSequenceId: restart.expectedSequenceId,
    } }) });
    return request('/workspace');
  });
  if (!workspace) throw new Error('Could not replace the unfinished Gambit chapter.');
  return workspace;
}
